import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tippspielAPI } from '../services/api';
import { formatDeadline } from '../utils/tipLabels';
import PageHeader from '../components/PageHeader';

const TippspielOverview = () => {
  const [overview, setOverview] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      tippspielAPI.getOverview(),
      tippspielAPI.getMatches({ tippableOnly: 'true' }),
    ])
      .then(([o, m]) => {
        setOverview(o.data);
        setMatches((m.data || []).slice(0, 5));
      })
      .catch((err) => setError(err.response?.data?.message || 'Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        kicker="Tippspiel"
        title="Übersicht"
        subtitle={overview?.projectName || 'Kein Projekt konfiguriert'}
        actions={(
          <>
            <Link to="/tippen" className="btn-wacker btn-wacker--sm">Tippen</Link>
            <Link to="/statistik" className="btn-wacker-outline btn-wacker--sm">Statistik</Link>
          </>
        )}
      />

      {error && <div className="alert-error-dark">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="stat-card-label">Deine Punkte</div>
          <div className="stat-card-value stat-card-value--accent">{overview?.myPoints ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Platzierung</div>
          <div className="stat-card-value">{overview?.myRank ? `${overview.myRank}.` : '–'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Offene Spiele</div>
          <div className="stat-card-value">{overview?.openMatches ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Tipps fehlen</div>
          <div className={`stat-card-value ${(overview?.missingTips ?? 0) > 0 ? 'text-amber-400' : ''}`}>
            {overview?.missingTips ?? 0}
          </div>
        </div>
      </div>

      {(overview?.missingTips ?? 0) > 0 && (
        <div className="alert-warning-dark">
          Du hast noch {overview.missingTips} offene Tipps.{' '}
          <Link to="/tippen" className="text-primary-light font-bold hover:underline">Jetzt tippen</Link>
        </div>
      )}

      <div className="glass-card p-6 md:p-7">
        <h2 className="panel-title">Nächste offene Spiele</h2>
        {matches.length === 0 ? (
          <p className="text-white/50">Aktuell keine offenen Spiele.</p>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {matches.map((m) => (
              <li key={m._id} className="flex justify-between items-center gap-4 py-4 first:pt-0 last:pb-0">
                <Link to="/tippen" className="group min-w-0">
                  <div className="font-semibold text-white group-hover:text-primary-light transition-colors truncate">
                    {m.homeTeamName}
                    <span className="text-white/35 font-normal mx-1.5">vs</span>
                    {m.awayTeamName}
                  </div>
                  {m.mfleagueRoundName && (
                    <div className="text-xs text-white/40 mt-0.5 uppercase tracking-wide">{m.mfleagueRoundName}</div>
                  )}
                </Link>
                <span className="text-xs text-white/45 shrink-0 font-medium">
                  {m.tipDeadlineAt
                    ? formatDeadline(m.tipDeadlineAt)
                    : m.kickoffAt && new Date(m.kickoffAt).toLocaleString('de-DE')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TippspielOverview;
